import axiosInstance from './axiosInstance';

const getAllMatches = async () => {
	try {
		const response = await axiosInstance.get('/matches');
		return response.data;
	} catch (error) {
		console.error('Error fetching matches:', error);
		throw error;
	}
};

const getMatchesWithoutTournament = async () => {
	try {
		const response = await axiosInstance.get('/matches/without-tournament');
		return response.data;
	} catch (error) {
		console.error('Error fetching matches without tournament:', error);
		throw error;
	}
};

const createMatch = async (match) => {
	try {
		const response = await axiosInstance.post('/matches', match);
		return response.data;
	} catch (error) {
		console.error('Error creating match:', error);
		throw error;
	}
};

const updateMatch = async (id, matchData) => {
	const response = await axiosInstance.put(`/matches/${id}`, matchData);
	if (response.status !== 200) {
		throw new Error('Failed to update match');
	}
	return response.data;
};

const getMatchById = async (id) => {
	const response = await axiosInstance.get(`/matches/${id}`);
	return response.data;
};

const processMatchResults = async (matchId) => {
	const response = await axiosInstance.post(`/matches/${matchId}/process-results`);
	return response.data;
};

const updateAndProcessMatch = async (matchId, matchData) => {
	await updateMatch(matchId, matchData);
	await processMatchResults(matchId);
};

const getMatchesBySportAndTournament = async (sportType, tournamentId) => {
  try {
	if (!tournamentId) {
      return await getFutureMatchesBySport(sportType);
    }
    const response = await axiosInstance.get(`/matches/by-sport-and-tournament`, {
      params: { sportType, tournamentId },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching matches by sport and tournament:', error);
    throw new Error('Failed to fetch matches by sport and tournament');
  }
};

const getMatchesBySport = async (sportType) => {
  try {
    const response = await axiosInstance.get(`/matches/by-sport`, {
      params: { sportType },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching matches by sport:', error);
    throw new Error('Failed to fetch matches by sport');
  }
};

const getFutureMatchesBySport = async (sportType) => {
  try {
    const response = await axiosInstance.get(`/matches/future-by-sport`, {
      params: { sportType },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching future matches by sport:', error);
    throw new Error('Failed to fetch future matches by sport');
  }
};

// Buď leagueId (soutěž), nebo teamId (týmový turnaj) — ne obojí.
const getMatchesFromExternal = async ({ leagueId, teamId, season }) => {
  const response = await axiosInstance.get('/matches/import/external', {
    params: { leagueId, teamId, season },
  });
  return response.data;
};

const importMatches = async ({ tournamentId, matches }) => {
  const response = await axiosInstance.post('/matches/import', {
    tournamentId,
    matches
  });
  return response.data;
};

const adminProcessMatchResults = async (matchId) => {
  try {
    const response = await axiosInstance.post(`/matches/${matchId}/process-results`);
    return response.data;
  } catch (error) {
    console.error('Error processing match results as admin:', error);
    throw error;
  }
};

const getMatchAnalysis = async (matchId, tournamentId) => {
	try {
		const response = await axiosInstance.get(`/matches/${matchId}/analysis`, {
			params: { tournamentId }
		});
		return response.data;
	} catch (error) {
		console.error('Error fetching AI analysis:', error);
		throw error;
	}
};

const getMatchPrediction = async (matchId, tournamentId) => {
	try {
		const response = await axiosInstance.get(`/matches/${matchId}/prediction`, {
			params: { tournamentId }
		});
		return response.data;
	} catch (error) {
		console.error(`Error fetching prediction for match ${matchId}:`, error);
		throw error;
	}
};

const getRemainingCredits = async (matchId, tournamentId) => {
	try {
		const response = await axiosInstance.get(`/matches/${matchId}/ai-credits`, {
			params: { tournamentId }
		});
		return response.data;
	} catch (error) {
		console.error(`Error fetching AI credits for match ${matchId}:`, error);
		return 0;
	}
};


const getMatchOdds = async (matchId) => {
	try {
		const response = await axiosInstance.get(`/matches/${matchId}/odds`);
		if (response.status === 204) return null;
		return response.data;
	} catch (error) {
		console.error(`Error fetching odds for match ${matchId}:`, error);
		throw error;
	}
};


const getMatchStatistics = async (matchId) => {
	try {
		const response = await axiosInstance.get(`/matches/${matchId}/statistics`);
		if (response.status === 204) return null;
		return response.data;
	} catch (error) {
		console.error(`Error fetching statistics for match ${matchId}:`, error);
		return null;
	}
};

// Souhrn statistik pro více zápasů najednou (dostupnost + červené karty).
// Nahrazuje volání getMatchStatistics na každé kartě zápasu zvlášť.
const getMatchStatisticsSummary = async (matchIds) => {
	if (!matchIds?.length) return [];
	try {
		const response = await axiosInstance.get('/matches/statistics/summary', {
			params: { matchIds: matchIds.join(',') }
		});
		return response.data || [];
	} catch (error) {
		console.error('Error fetching match statistics summary:', error);
		return [];
	}
};

const getMatchEvents = async (matchId) => {
	try {
		const response = await axiosInstance.get(`/matches/${matchId}/events`);
		if (response.status === 204) return null;
		return response.data;
	} catch (error) {
		console.error(`Error fetching events for match ${matchId}:`, error);
		return null;
	}
};

const getLandingPageMatches = async () => {
  try {
    const response = await axiosInstance.get('/matches/landing-page');
    return response.data;
  } catch (error) {
    console.error('Failed to load landing page matches:', error);
    return [];
  }
};

export default {
	getAllMatches,
	getMatchById,
	createMatch,
	updateMatch,
	processMatchResults,
	updateAndProcessMatch,
	getMatchesBySportAndTournament,
	getMatchesBySport,
	getFutureMatchesBySport,
	getMatchesWithoutTournament,
	getMatchesFromExternal,
	importMatches,
  getMatchAnalysis,
  getMatchStatistics,
  getMatchStatisticsSummary,
  getMatchPrediction,
  getMatchOdds,
  getMatchEvents,
  getRemainingCredits,
	adminProcessMatchResults,
	getLandingPageMatches
};